import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pin,
  Pencil,
  ChevronDown,
  Search,
  ArrowLeftRight,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useBrand } from "@/contexts/BrandContext";
import { useBrandCompletion } from "@/hooks/useBrandCompletion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

const TRIGGER_MAX = 20;
const CURRENT_MAX = 16;
const LIST_MAX = 20;

const cap = (text: string, max: number) =>
  text.length > max ? `${text.substring(0, max)}…` : text;

interface BrandSwitcherPopoverProps {
  collapsed: boolean;
}

export function BrandSwitcherPopover({ collapsed }: BrandSwitcherPopoverProps) {
  const navigate = useNavigate();
  const { activeBrand, brands, setActiveBrand } = useBrand();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fullBrandName = activeBrand?.business_name || "Select Brand";
  // The prototype puts a completion percentage under the brand name here (brandSub,
  // app.html:5938). The hook wraps the same function the Dashboard's COMPLETION column
  // uses, so the two can never disagree about one brand.
  const completion = useBrandCompletion(activeBrand);
  const brandName = cap(fullBrandName, TRIGGER_MAX);

  // Read previous brand from localStorage
  const previousBrandId = localStorage.getItem("previous-brand-id");
  const previousBrand = useMemo(
    () => (previousBrandId ? brands.find((b) => b.id === previousBrandId && b.id !== activeBrand?.id) : null),
    [previousBrandId, brands, activeBrand?.id]
  );

  // Filter brands for "All Brands" section (exclude active + previous)
  const otherBrands = useMemo(() => {
    const excludeIds = new Set([activeBrand?.id, previousBrand?.id].filter(Boolean));
    return brands
      .filter((b) => !excludeIds.has(b.id))
      .filter((b) =>
        searchQuery
          ? (b.business_name || "").toLowerCase().includes(searchQuery.toLowerCase())
          : true
      );
  }, [brands, activeBrand?.id, previousBrand?.id, searchQuery]);

  const handleSwitchBrand = async (brandId: string) => {
    if (activeBrand?.id) {
      localStorage.setItem("previous-brand-id", activeBrand.id);
    }
    await setActiveBrand(brandId);
    setOpen(false);
    setSearchQuery("");
  };

  if (collapsed) return null;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearchQuery(""); }}>
      <PopoverTrigger asChild>
        <button
          className="mx-3 mt-3 flex h-11 w-[calc(100%-1.5rem)] items-center gap-2.5 rounded-[11px] border border-brand-gold/50 bg-[#fff9f0] px-3 py-2 text-left transition-colors hover:border-brand-gold"
          title={fullBrandName}
        >
          <Pin className="h-4 w-4 shrink-0 text-brand-gold" />
          <span className="flex min-w-0 flex-1 flex-col items-start leading-tight">
            <span className="w-full truncate text-[13px] font-bold text-brand-deep-purple">
              {brandName}
            </span>
            {completion !== null && (
              <span className="w-full truncate text-[10px] font-medium text-brand-deep-purple/70 tabular-nums">
                {completion}% Complete
              </span>
            )}
          </span>
          <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-[#caa24f] transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        collisionPadding={8}
        className="z-50 flex max-h-[var(--radix-popover-content-available-height)] w-[272px] flex-col overflow-hidden rounded-[14px] border border-border bg-card p-2.5 text-foreground shadow-[0_24px_60px_-20px_rgba(36,12,42,0.5)]"
        sideOffset={6}
      >
        {/* Search */}
        <div className="relative mb-1.5 shrink-0">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
          <input
            type="text"
            placeholder="Search brands"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 w-full rounded-[9px] border border-border bg-muted/60 pl-8 pr-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-purple/30"
            autoFocus
          />
        </div>

        <ScrollArea className="min-h-0 flex-1 max-h-[min(300px,40vh)] overscroll-contain">
          {/* Current Brand */}
          {activeBrand && !searchQuery && (
            <div className="pb-1">
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current brand</p>
              <div className="flex min-w-0 w-full items-center gap-2 rounded-[9px] bg-brand-purple/10 px-2 py-1.5">
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground" title={activeBrand.business_name || "Unnamed Brand"}>
                  {cap(activeBrand.business_name || "Unnamed Brand", CURRENT_MAX)}
                </span>
                <button
                  onClick={() => { navigate("/brand-guide"); setOpen(false); }}
                  className="ml-1 shrink-0 text-xs font-semibold text-primary hover:underline"
                >
                  Edit
                </button>
              </div>
            </div>
          )}

          {/* Previous Brand */}
          {previousBrand && !searchQuery && (
            <div className="pb-1">
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Previous</p>
              <button
                onClick={() => handleSwitchBrand(previousBrand.id)}
                className="flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-left text-foreground/80 transition-colors hover:bg-muted"
              >
                <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-[13px]" title={previousBrand.business_name || "Unnamed Brand"}>
                  {cap(previousBrand.business_name || "Unnamed Brand", LIST_MAX)}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">Switch</span>
              </button>
            </div>
          )}

          {/* All Brands */}
          {otherBrands.length > 0 && (
            <div className="pb-1">
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {searchQuery ? "Results" : "All brands"}
              </p>
              {otherBrands.map((brand) => (
                <button
                  key={brand.id}
                  onClick={() => handleSwitchBrand(brand.id)}
                  className="flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-left text-foreground/80 transition-colors hover:bg-muted"
                >
                  <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate text-[13px]" title={brand.business_name || "Unnamed Brand"}>
                    {cap(brand.business_name || "Unnamed Brand", LIST_MAX)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {searchQuery && otherBrands.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">No brands found</p>
            </div>
          )}
        </ScrollArea>

        {/* Footer (prototype: Edit "The Brain" + Manage brands) */}
        <div className="mt-2 shrink-0 border-t border-border pt-2">
          <button
            onClick={() => { navigate("/brand-guide"); setOpen(false); }}
            className="flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-[13px] font-semibold text-primary transition-colors hover:bg-muted"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span>Edit "The Brain"</span>
          </button>
          <button
            onClick={() => { navigate("/dashboard"); setOpen(false); }}
            className="flex w-full items-center gap-2 rounded-[9px] px-2 py-1.5 text-[13px] font-semibold text-primary transition-colors hover:bg-muted"
          >
            <Pin className="h-3.5 w-3.5" />
            <span>Manage brands</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
